import type { FreightRateRow } from '@api/useDriverFreightRates/types/shared'
import { useMetadata } from '@api/useMetadata'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import type { AgGridReactProps } from 'ag-grid-react'
import React, { useMemo, useRef } from 'react'

import { useDriverFreightRates } from '../../api/useDriverFreightRates'
import { getColumnDefs } from './columnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'pay-mileage-bands'

const excelOptions = [
  {
    title: 'Download Pay Mileage Bands',
    fileName: 'Pay Mileage Bands.xlsx',
    downloadEP: api.downloadDriverFreightRates,
  },
  { title: 'Upload Pay Mileage Bands', uploadEP: api.uploadDriverFreightRates },
]

export function Grid({ setSelectedRow, tableId, canEdit }) {
  const gridApiRef = useRef(null)
  const { updateSetting } = useUserSettings()
  const { useDriverFreightRatesQuery, driverRatesUpdateMutation } = useDriverFreightRates()
  const { data: rateMetadata } = useMetadata('payroll/driver_rate')
  const { mutate } = driverRatesUpdateMutation()
  const { data, isPending, isFetching, refetch } = useDriverFreightRatesQuery()
  const columnDefs = useMemo(() => getColumnDefs(rateMetadata), [rateMetadata])
  const queryClient = useQueryClient()

  const agPropOverrides: AgGridReactProps = {
    getRowId: (row) => row?.data?.id,
    rowSelection: 'single',
    onFirstDataRendered: (params) => params.api.getDisplayedRowAtIndex(0).setSelected(true),
  }

  const rowData = useMemo(
    () =>
      tableId && data?.rows ? data?.rows?.filter((item) => item.id === tableId) : (data?.rows && data?.rows) || [],
    [data?.rows, tableId]
  )

  const update = async (driverRate: FreightRateRow) => {
    mutate(driverRate)
    setSelectedRow({ ...driverRate })
  }

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Pay Mileage Bands',
        actionButtons: (
          // @ts-ignore : TODO: Make UploadDownloadButton props optional in GraviGrid
          <UploadDownloadButtons
            refreshCallback={() => {
              gridApiRef.current.deselectAll()
              setSelectedRow((prev) => {})
              queryClient
                .invalidateQueries({
                  queryKey: ['driverFreightRatesQuery'],
                })
                .then(() => gridApiRef.current.getDisplayedRowAtIndex(0).setSelected(true))
            }}
            excelOptions={excelOptions}
            fileName='Pay Mileage Bands.xlsx'
          />
        ),
      }}
      loading={isPending || isFetching}
      updateEP={canEdit && update}
      externalRef={gridApiRef}
      storageKey={STORAGE_KEY}
      columnDefs={columnDefs}
      rowData={rowData}
      agPropOverrides={agPropOverrides}
      onSelectionChanged={(e) => setSelectedRow(e.api.getSelectedRows()[0])}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
